/**
 * Offline renderer — processes a loop through the full DSP chain using
 * OfflineAudioContext.
 *
 * Uses the warm-render strategy: renders (WARMUP_ITERATIONS + 1)× the loop
 * length so stateful processors (compressor, limiter, filters, transient RMS,
 * delay lines) settle before the exported slice begins.
 *
 * The total render length is an exact multiple of the loop length.  This
 * guarantees the trim start lands on a loop boundary (buffer sample 0),
 * producing output that is sample-aligned with the source — matching
 * real-time playback precisely.
 *
 * Output is trimmed to exactly the source buffer's sample length.
 */

import {
  WORKLET_EQP,
  WORKLET_COMP,
  WORKLET_MB,
  WORKLET_OD,
  WORKLET_TRANS,
  WORKLET_DELAY,
  WORKLET_LIM,
  WORKLET_REV,
  WORKLET_RS_HALL,
  type EngineStateSnapshot,
} from './engine'

/**
 * Number of warmup iterations before the output slice.
 * Stateful processors (compressor envelopes, limiter gain reduction,
 * transient RMS windows, crossover filter state, delay lines) all converge
 * within the first 1-2 loop passes.  Two warmup iterations are sufficient
 * for typical settings; the third is the output we keep.
 */
const WARMUP_ITERATIONS = 2

function dbToGain(db: number): number {
  if (db === Number.NEGATIVE_INFINITY) return 0
  return Math.pow(10, db / 20)
}

function divisionBeats(div: string): number {
  const d = String(div ?? '').trim().replace(/d$/, 'D').replace(/t$/, 'T')
  switch (d) {
    case '1/1': return 4
    case '1/2': return 2
    case '1/4': return 1
    case '1/4D': return 1.5
    case '1/8': return 0.5
    case '1/8D': return 0.75
    case '1/8T': return 1 / 3
    case '1/16': return 0.25
    case '1/16D': return 0.375
    case '1/16T': return 1 / 6
    case '1/32': return 0.125
    case '1/32D': return 0.1875
    case '1/32T': return 1 / 12
    default: return 1
  }
}

interface RenderOptions {
  extendReverbTail?: boolean
  reverbTailThreshold?: number
}

/**
 * Render the given AudioBuffer through the DSP chain described by `state`.
 * Returns a trimmed AudioBuffer that is exactly `sourceBuffer.length` samples
 * (plus optional tail extension for reverb/delay decay).
 */
export async function renderOffline(
  sourceBuffer: AudioBuffer,
  state: EngineStateSnapshot,
  options: RenderOptions = {},
): Promise<AudioBuffer> {
  const { extendReverbTail = false, reverbTailThreshold = -75 } = options
  const sampleRate = sourceBuffer.sampleRate
  const numChannels = sourceBuffer.numberOfChannels
  const loopLengthSamples = sourceBuffer.length

  // ─── Compute render length ───────────────────────────────────────────────
  //
  // Always use full warmup.  The warmup iterations let ALL stateful
  // processors (compressor, delay, reverb, etc.) reach steady-state before
  // the output slice begins.  The delay line runs continuously through
  // warmup with the correct parameters — its timing is preserved because
  // the loop repeats are sample-aligned.
  const totalIterations = WARMUP_ITERATIONS + 1
  const baseSamples = loopLengthSamples * totalIterations
  let totalSamples = baseSamples

  // ─── Extended FX tail (reverb / delay feedback) ──────────────────────────
  let extendedSamples = 0
  if (extendReverbTail) {
    let maxTailSeconds = 0

    if (state.revEnabled || state.rsEnabled) {
      const tailFactor = state.revEnabled ? state.revDecay : state.rsFeedback
      const reverbTailSeconds = Math.min(30, tailFactor * 15)
      maxTailSeconds = Math.max(maxTailSeconds, reverbTailSeconds)
    }

    if (state.delayEnabled && state.delayFeedback > 0) {
      const delaySeconds = state.delaySyncEnabled
        ? (60 / Math.max(1e-3, state.delayBpm)) * divisionBeats(state.delayDivision)
        : (state.delayMs / 1000)
      const fb = Math.max(1e-3, Math.min(0.999, state.delayFeedback))
      const repeatsToMinus60 = Math.log(0.001) / Math.log(fb)
      const delayTailSeconds = Math.min(30, Math.max(0, repeatsToMinus60) * delaySeconds)
      maxTailSeconds = Math.max(maxTailSeconds, delayTailSeconds)
    }

    if (maxTailSeconds > 0) {
      extendedSamples = Math.floor(maxTailSeconds * sampleRate)
      totalSamples += extendedSamples
    }
  }

  const baseDuration = baseSamples / sampleRate

  // ─── Create OfflineAudioContext ──────────────────────────────────────────
  const ctx = new OfflineAudioContext(numChannels, totalSamples, sampleRate)

  // ─── Load worklets ───────────────────────────────────────────────────────
  await ctx.audioWorklet.addModule(WORKLET_EQP)
  await ctx.audioWorklet.addModule(WORKLET_COMP)
  await ctx.audioWorklet.addModule(WORKLET_MB)
  await ctx.audioWorklet.addModule(WORKLET_OD)
  await ctx.audioWorklet.addModule(WORKLET_TRANS)
  await ctx.audioWorklet.addModule(WORKLET_DELAY)
  await ctx.audioWorklet.addModule(WORKLET_LIM)
  await ctx.audioWorklet.addModule(WORKLET_REV)
  await ctx.audioWorklet.addModule(WORKLET_RS_HALL)

  // ─── Create gain nodes ──────────────────────────────────────────────────
  const eqDryGain = ctx.createGain()
  const eqWetGain = ctx.createGain()
  const eqMixGain = ctx.createGain()
  const mbDryGain = ctx.createGain()
  const mbWetGain = ctx.createGain()
  const mbMixGain = ctx.createGain()
  const compDryGain = ctx.createGain()
  const compWetGain = ctx.createGain()
  const compMixGain = ctx.createGain()
  const odDryGain = ctx.createGain()
  const odWetGain = ctx.createGain()
  const odMixGain = ctx.createGain()
  const transDryGain = ctx.createGain()
  const transWetGain = ctx.createGain()
  const transMixGain = ctx.createGain()
  const delayDryGain = ctx.createGain()
  const delayWetGain = ctx.createGain()
  const delayMixGain = ctx.createGain()
  const rsDryGain = ctx.createGain()
  const rsWetGain = ctx.createGain()
  const rsMixGain = ctx.createGain()
  const revDryGain = ctx.createGain()
  const revWetGainA = ctx.createGain()
  const revMixGain = ctx.createGain()
  const limDryGain = ctx.createGain()
  const limWetGain = ctx.createGain()
  const limMixGain = ctx.createGain()
  const masterGain = ctx.createGain()
  const outputGain = ctx.createGain()

  // Mix bus gains (always unity)
  eqMixGain.gain.value = 1
  compMixGain.gain.value = 1
  mbMixGain.gain.value = 1
  odMixGain.gain.value = 1
  transMixGain.gain.value = 1
  delayMixGain.gain.value = 1
  rsMixGain.gain.value = 1
  revMixGain.gain.value = 1
  limMixGain.gain.value = 1

  // Per-module dry/wet from snapshot
  eqDryGain.gain.value = dbToGain(state.eqDryDb)
  eqWetGain.gain.value = dbToGain(state.eqWetDb)
  compDryGain.gain.value = dbToGain(state.compDryDb)
  compWetGain.gain.value = dbToGain(state.compWetDb)
  mbDryGain.gain.value = dbToGain(state.mbDryDb)
  mbWetGain.gain.value = dbToGain(state.mbWetDb)
  odDryGain.gain.value = dbToGain(state.odDryDb)
  odWetGain.gain.value = dbToGain(state.odWetDb)
  transDryGain.gain.value = dbToGain(state.transDryDb)
  transWetGain.gain.value = dbToGain(state.transWetDb)

  // Delay dry/wet — when disabled, pass-through (dry=1, wet=0)
  delayDryGain.gain.value = state.delayEnabled ? dbToGain(state.delayDryDb) : 1
  delayWetGain.gain.value = state.delayEnabled ? dbToGain(state.delayWetDb) : 0

  // RS / Reverb dry/wet — when disabled, pass-through
  rsDryGain.gain.value = state.rsEnabled ? dbToGain(state.rsDryDb) : 1
  rsWetGain.gain.value = state.rsEnabled ? dbToGain(state.rsWetDb) : 0
  revDryGain.gain.value = state.revEnabled ? dbToGain(state.revDryDb) : 1
  revWetGainA.gain.value = state.revEnabled ? dbToGain(state.revWetDb) : 0

  limDryGain.gain.value = dbToGain(state.limDryDb)
  limWetGain.gain.value = dbToGain(state.limWetDb)
  masterGain.gain.value = dbToGain(state.masterDb)
  outputGain.gain.value = dbToGain(state.outputTrimDb)

  // ─── Worklet nodes ──────────────────────────────────────────────────────
  const eqNode = new AudioWorkletNode(ctx, 'eqp-wdf-processor')
  const compNode = new AudioWorkletNode(ctx, 'compressor-processor')
  const mbNode = new AudioWorkletNode(ctx, 'multiband-processor', {
    numberOfOutputs: 2, outputChannelCount: [2, 2],
  })
  const odNode = new AudioWorkletNode(ctx, 'overdrive-processor', {
    numberOfOutputs: 2, outputChannelCount: [2, 2],
  })
  const transNode = new AudioWorkletNode(ctx, 'transient-processor', {
    numberOfOutputs: 2, outputChannelCount: [2, 2],
  })
  // Pass ALL delay params via processorOptions so they are available
  // synchronously in the constructor — guarantees correct state from
  // the very first process() call in OfflineAudioContext.
  const delayNode = new AudioWorkletNode(ctx, 'delay-processor', {
    numberOfOutputs: 2, outputChannelCount: [2, 2],
    processorOptions: {
      enabled: state.delayEnabled,
      mode: state.delayMode,
      width: state.delayWidth,
      delayMs: state.delayMs,
      feedback: state.delayFeedback,
      mix: state.delayMix,
      syncEnabled: state.delaySyncEnabled,
      bpm: state.delayBpm,
      division: state.delayDivision,
      tone: state.delayTone,
      saturation: state.delaySaturation,
    },
  })
  // Reverbs also take processorOptions so the first process() call already
  // runs with final parameters (no postMessage race in OfflineAudioContext).
  const rsNode = new AudioWorkletNode(ctx, 'rs-hall-processor', {
    numberOfOutputs: 1, outputChannelCount: [2],
    processorOptions: {
      roomSize: state.rsRoomSize,
      preDelayMs: state.rsPreDelayMs,
      feedback: state.rsFeedback,
      damping: state.rsDamping,
      diffusion: state.rsDiffusion,
      earlySize: state.rsEarlySize,
      earlyDecay: state.rsEarlyDecay,
      modRate: state.rsModRate,
      modDepth: state.rsModDepth,
      highCut: state.rsHighCut,
      lowCut: state.rsLowCut,
      earlyLateMix: state.rsEarlyLateMix,
      outputDb: state.rsOutputDb,
    },
  })
  const revNode = new AudioWorkletNode(ctx, 'reverb-processor', {
    numberOfOutputs: 1, outputChannelCount: [2],
    processorOptions: {
      predelayMs: state.revPredelayMs,
      bandwidth: state.revBandwidth,
      decay: state.revDecay,
      damping: state.revDamping,
      density: state.revDensity,
      size: state.revSize,
      earlyLate: state.revEarlyLate,
      modRate: state.revModRate,
      modDepth: state.revModDepth,
      outputDb: state.revOutputDb,
    },
  })
  const limNode = new AudioWorkletNode(ctx, 'limiter-processor', {
    numberOfOutputs: 2, outputChannelCount: [2, 2],
  })

  // ─── Send parameters to worklets ────────────────────────────────────────
  //
  // IMPORTANT: Delay params are sent in the SAME order as engine.ts
  // createGraph() so the worklet converges to identical state.
  //
  // Diagnostic: log delay state reaching the offline renderer
  if (state.delayEnabled) {
    const effectiveMs = state.delaySyncEnabled
      ? (60 / Math.max(1e-3, state.delayBpm)) * divisionBeats(state.delayDivision) * 1000
      : state.delayMs
    console.log('[Offline Delay]', {
      enabled: state.delayEnabled,
      syncEnabled: state.delaySyncEnabled,
      bpm: state.delayBpm,
      division: state.delayDivision,
      delayMs: state.delayMs,
      effectiveMs: Math.round(effectiveMs * 100) / 100,
      feedback: state.delayFeedback,
      mix: state.delayMix,
      mode: state.delayMode,
      dryDb: state.delayDryDb,
      wetDb: state.delayWetDb,
    })
  }

  // EQ
  eqNode.port.postMessage({ type: 'lowGain', value: state.lowShelfDb })
  eqNode.port.postMessage({ type: 'mid1Gain', value: state.mid1Db })
  eqNode.port.postMessage({ type: 'mid2Gain', value: state.mid2Db })
  eqNode.port.postMessage({ type: 'highGain', value: state.highShelfDb })
  eqNode.port.postMessage({ type: 'lowFreq', value: state.lowShelfFreq })
  eqNode.port.postMessage({ type: 'mid1Freq', value: state.mid1Freq })
  eqNode.port.postMessage({ type: 'mid2Freq', value: state.mid2Freq })
  eqNode.port.postMessage({ type: 'highFreq', value: state.highShelfFreq })
  eqNode.port.postMessage({ type: 'mid1Q', value: state.mid1Q })
  eqNode.port.postMessage({ type: 'mid2Q', value: state.mid2Q })
  eqNode.port.postMessage({ type: 'heat', value: state.heatAmount })

  // Compressor
  compNode.port.postMessage({ type: 'compType', value: state.compType })
  compNode.port.postMessage({ type: 'threshold', value: state.compThresholdDb })
  compNode.port.postMessage({ type: 'ratio', value: state.compRatio })
  compNode.port.postMessage({ type: 'attack', value: state.compAttackMs })
  compNode.port.postMessage({ type: 'release', value: state.compReleaseMs })
  compNode.port.postMessage({ type: 'makeup', value: state.compMakeupDb })
  compNode.port.postMessage({ type: 'knee', value: state.compKnee })
  compNode.port.postMessage({ type: 'distAmount', value: state.compDistAmount })
  compNode.port.postMessage({ type: 'distMode', value: state.compDistMode })
  compNode.port.postMessage({ type: 'distPosition', value: state.compDistPosition })

  // Multiband
  mbNode.port.postMessage({ type: 'lowMidFreq', value: state.mbLowMidFreq })
  mbNode.port.postMessage({ type: 'midHighFreq', value: state.mbMidHighFreq })
  mbNode.port.postMessage({ type: 'lowGain', value: state.mbLowGainDb })
  mbNode.port.postMessage({ type: 'midGain', value: state.mbMidGainDb })
  mbNode.port.postMessage({ type: 'highGain', value: state.mbHighGainDb })
  mbNode.port.postMessage({ type: 'lowDrive', value: state.mbLowDrive })
  mbNode.port.postMessage({ type: 'midDrive', value: state.mbMidDrive })
  mbNode.port.postMessage({ type: 'highDrive', value: state.mbHighDrive })
  mbNode.port.postMessage({ type: 'lowType', value: state.mbLowType })
  mbNode.port.postMessage({ type: 'midType', value: state.mbMidType })
  mbNode.port.postMessage({ type: 'highType', value: state.mbHighType })
  mbNode.port.postMessage({ type: 'globalDrive', value: state.mbGlobalDrive })

  // Overdrive
  odNode.port.postMessage({ type: 'driveType', value: state.odDriveType })
  odNode.port.postMessage({ type: 'drive', value: state.odDriveAmount })
  odNode.port.postMessage({ type: 'tightHz', value: state.odTightHz })
  odNode.port.postMessage({ type: 'toneHz', value: state.odToneHz })
  odNode.port.postMessage({ type: 'bias', value: state.odBias })
  odNode.port.postMessage({ type: 'outputDb', value: state.odOutputDb })

  // Transient
  transNode.port.postMessage({ type: 'attackGain', value: state.transAttackGainDb })
  transNode.port.postMessage({ type: 'sustainGain', value: state.transSustainGainDb })
  transNode.port.postMessage({ type: 'holdTime', value: state.transHoldMs })
  transNode.port.postMessage({ type: 'decay', value: state.transDecayMs })
  transNode.port.postMessage({ type: 'outputDb', value: state.transOutputDb })

  // Delay — SAME order as engine.ts createGraph()
  delayNode.port.postMessage({ type: 'enabled', value: state.delayEnabled })
  delayNode.port.postMessage({ type: 'mode', value: state.delayMode })
  delayNode.port.postMessage({ type: 'width', value: state.delayWidth })
  delayNode.port.postMessage({ type: 'delayMs', value: state.delayMs })
  delayNode.port.postMessage({ type: 'feedback', value: state.delayFeedback })
  delayNode.port.postMessage({ type: 'mix', value: state.delayMix })
  delayNode.port.postMessage({ type: 'syncEnabled', value: state.delaySyncEnabled })
  delayNode.port.postMessage({ type: 'bpm', value: state.delayBpm })
  delayNode.port.postMessage({ type: 'division', value: state.delayDivision })
  delayNode.port.postMessage({ type: 'tone', value: state.delayTone })
  delayNode.port.postMessage({ type: 'saturation', value: state.delaySaturation })

  // Reverb A
  revNode.port.postMessage({ type: 'room-type', value: state.revRoomType })
  revNode.port.postMessage({ type: 'predelay', value: state.revPredelayMs })
  revNode.port.postMessage({ type: 'size', value: state.revSize })
  revNode.port.postMessage({ type: 'decay', value: state.revDecay })
  revNode.port.postMessage({ type: 'damping', value: state.revDamping })
  revNode.port.postMessage({ type: 'bandwidth', value: state.revBandwidth })
  revNode.port.postMessage({ type: 'density', value: state.revDensity })
  revNode.port.postMessage({ type: 'early-late', value: state.revEarlyLate })
  revNode.port.postMessage({ type: 'mod-rate', value: state.revModRate })
  revNode.port.postMessage({ type: 'mod-depth', value: state.revModDepth })
  revNode.port.postMessage({ type: 'output-db', value: state.revOutputDb })

  // RS Hall
  rsNode.port.postMessage({ type: 'roomSize', value: state.rsRoomSize })
  rsNode.port.postMessage({ type: 'preDelay', value: state.rsPreDelayMs })
  rsNode.port.postMessage({ type: 'feedback', value: state.rsFeedback })
  rsNode.port.postMessage({ type: 'damping', value: state.rsDamping })
  rsNode.port.postMessage({ type: 'diffusion', value: state.rsDiffusion })
  rsNode.port.postMessage({ type: 'earlySize', value: state.rsEarlySize })
  rsNode.port.postMessage({ type: 'earlyDecay', value: state.rsEarlyDecay })
  rsNode.port.postMessage({ type: 'modRate', value: state.rsModRate })
  rsNode.port.postMessage({ type: 'modDepth', value: state.rsModDepth })
  rsNode.port.postMessage({ type: 'highCut', value: state.rsHighCut })
  rsNode.port.postMessage({ type: 'lowCut', value: state.rsLowCut })
  rsNode.port.postMessage({ type: 'earlyLateMix', value: state.rsEarlyLateMix })
  rsNode.port.postMessage({ type: 'output-db', value: state.rsOutputDb })

  // Limiter
  limNode.port.postMessage({ type: 'threshold', value: state.limThresholdDb })
  limNode.port.postMessage({ type: 'ceiling', value: state.limCeilingDb })
  limNode.port.postMessage({ type: 'release', value: state.limReleaseMs })
  limNode.port.postMessage({ type: 'knee', value: state.limKnee })
  limNode.port.postMessage({ type: 'mode', value: state.limMode })
  limNode.port.postMessage({ type: 'quality', value: state.limQuality })

  // ─── Create looping source ──────────────────────────────────────────────
  const source = ctx.createBufferSource()
  source.buffer = sourceBuffer
  source.loop = true

  // ─── Wire graph (mirrors engine.ts reconnectSource exactly) ─────────────
  let lastNode: AudioNode

  if (state.bypassed) {
    lastNode = source
  } else {
    // EQ
    let eqOutput: AudioNode
    if (state.eqEnabled) {
      source.connect(eqDryGain)
      source.connect(eqNode)
      eqNode.connect(eqWetGain)
      eqDryGain.connect(eqMixGain)
      eqWetGain.connect(eqMixGain)
      eqOutput = eqMixGain
    } else {
      eqOutput = source
    }

    // Multiband
    let mbOutput: AudioNode
    if (state.mbEnabled) {
      eqOutput.connect(mbNode)
      mbNode.connect(mbDryGain, 0)
      mbNode.connect(mbWetGain, 1)
      mbDryGain.connect(mbMixGain)
      mbWetGain.connect(mbMixGain)
      mbOutput = mbMixGain
    } else {
      mbOutput = eqOutput
    }

    // Compressor
    let compOutput: AudioNode
    if (state.compEnabled) {
      mbOutput.connect(compDryGain)
      mbOutput.connect(compNode)
      compNode.connect(compWetGain)
      compDryGain.connect(compMixGain)
      compWetGain.connect(compMixGain)
      compOutput = compMixGain
    } else {
      compOutput = mbOutput
    }

    // Overdrive
    let odOutput: AudioNode
    if (state.odEnabled) {
      compOutput.connect(odNode)
      odNode.connect(odDryGain, 0)
      odNode.connect(odWetGain, 1)
      odDryGain.connect(odMixGain)
      odWetGain.connect(odMixGain)
      odOutput = odMixGain
    } else {
      odOutput = compOutput
    }

    // Transient
    let transOutput: AudioNode
    if (state.transEnabled) {
      odOutput.connect(transNode)
      transNode.connect(transDryGain, 0)
      transNode.connect(transWetGain, 1)
      transDryGain.connect(transMixGain)
      transWetGain.connect(transMixGain)
      transOutput = transMixGain
    } else {
      transOutput = odOutput
    }

    // Delay (parallel dry/wet — identical topology to engine.ts)
    let delayOutput: AudioNode
    if (state.delayEnabled) {
      transOutput.connect(delayNode)
      delayNode.connect(delayDryGain, 0)
      delayNode.connect(delayWetGain, 1)
      delayDryGain.connect(delayMixGain)
      delayWetGain.connect(delayMixGain)
      delayOutput = delayMixGain
    } else {
      delayOutput = transOutput
    }

    // RS Hall Reverb
    let rsOutput: AudioNode
    if (state.rsEnabled) {
      delayOutput.connect(rsDryGain)
      delayOutput.connect(rsNode)
      rsNode.connect(rsWetGain)
      rsDryGain.connect(rsMixGain)
      rsWetGain.connect(rsMixGain)
      rsOutput = rsMixGain
    } else {
      rsOutput = delayOutput
    }

    // Reverb A
    let revOutput: AudioNode
    if (state.revEnabled) {
      rsOutput.connect(revDryGain)
      rsOutput.connect(revNode)
      revNode.connect(revWetGainA)
      revDryGain.connect(revMixGain)
      revWetGainA.connect(revMixGain)
      revOutput = revMixGain
    } else {
      revOutput = rsOutput
    }

    // Limiter
    let limOutput: AudioNode
    if (state.limEnabled) {
      revOutput.connect(limNode)
      limNode.connect(limDryGain, 0)
      limNode.connect(limWetGain, 1)
      limDryGain.connect(limMixGain)
      limWetGain.connect(limMixGain)
      limOutput = limMixGain
    } else {
      limOutput = revOutput
    }

    lastNode = limOutput
  }

  // Master → optional master limiter → output trim → destination
  lastNode.connect(masterGain)
  let tapSource: AudioNode = masterGain

  if (!state.bypassed && state.masterLimiterEnabled) {
    const masterLimiterNode = ctx.createDynamicsCompressor()
    masterLimiterNode.threshold.value = state.masterLimiterThresholdDb
    masterLimiterNode.knee.value = 0
    masterLimiterNode.ratio.value = 20
    masterLimiterNode.attack.value = 0.003
    masterLimiterNode.release.value = 0.1
    masterGain.connect(masterLimiterNode)
    tapSource = masterLimiterNode
  }

  tapSource.connect(outputGain)
  outputGain.connect(ctx.destination)

  // ─── Schedule source ────────────────────────────────────────────────────
  source.start(0)
  source.stop(baseDuration)

  // ─── Suspend / resume to guarantee postMessage delivery ─────────────────
  //
  // OfflineAudioContext processes audio at maximum speed.  postMessage is
  // async — messages queue but may not reach the worklet before the first
  // process() call.  Fix:
  //
  //   1. Suspend at one render quantum (128 samples)
  //   2. Start rendering — processes 128 samples then suspends
  //   3. Yield to the event loop so the MessagePort queue flushes
  //   4. Resume — all subsequent audio uses correct parameters
  //
  // The first 128 samples fall within the warmup iterations and are never
  // included in the output.
  const suspendTime = 128 / sampleRate
  const suspendPromise = ctx.suspend(suspendTime)

  const renderPromise = ctx.startRendering()

  await suspendPromise
  await new Promise<void>(resolve => setTimeout(resolve, 0))
  await ctx.resume()

  const rendered = await renderPromise

  // ─── Trim output ────────────────────────────────────────────────────────
  const trimStart = WARMUP_ITERATIONS * loopLengthSamples

  // Extended tail detection (shared by reverb and delay)
  let actualOutputLength = loopLengthSamples
  if (extendReverbTail && extendedSamples > 0) {
    const thresholdGain = dbToGain(reverbTailThreshold)
    const thresholdGainSquared = thresholdGain * thresholdGain
    const windowSize = Math.max(1, Math.floor(sampleRate * 0.1))
    const minSilenceDuration = Math.floor(sampleRate * 0.5)

    const searchStart = trimStart + loopLengthSamples
    const searchEnd = Math.min(rendered.length, trimStart + loopLengthSamples + extendedSamples)

    let tailEnd = searchEnd
    let silentSamples = 0

    for (let windowStart = searchStart; windowStart < searchEnd; windowStart += windowSize) {
      const windowEnd = Math.min(searchEnd, windowStart + windowSize)
      let windowPeakSquared = 0

      for (let i = windowStart; i < windowEnd; i++) {
        for (let ch = 0; ch < numChannels; ch++) {
          const sample = rendered.getChannelData(ch)[i]
          const sampleSquared = sample * sample
          if (sampleSquared > windowPeakSquared) windowPeakSquared = sampleSquared
        }
      }

      if (windowPeakSquared < thresholdGainSquared) {
        silentSamples += (windowEnd - windowStart)
        if (silentSamples >= minSilenceDuration) {
          tailEnd = windowEnd - (silentSamples - minSilenceDuration)
          break
        }
      } else {
        silentSamples = 0
      }
    }

    // Snap to zero crossing
    if (tailEnd > searchStart) {
      const data = rendered.getChannelData(0)
      let zc = tailEnd
      const zcWindow = Math.min(sampleRate, tailEnd - searchStart)
      for (let i = 0; i < zcWindow; i++) {
        const idx = tailEnd - i
        if (idx <= searchStart) break
        if ((data[idx] >= 0 && data[idx - 1] < 0) || (data[idx] <= 0 && data[idx - 1] > 0)) {
          zc = idx
          break
        }
      }
      tailEnd = zc
    }

    actualOutputLength = Math.max(loopLengthSamples, tailEnd - trimStart)
  }

  // Extract the output slice
  const outputLength = actualOutputLength
  const output = new AudioBuffer({ numberOfChannels: numChannels, length: outputLength, sampleRate })

  for (let ch = 0; ch < numChannels; ch++) {
    const renderedData = rendered.getChannelData(ch)
    const outputData = output.getChannelData(ch)
    for (let i = 0; i < outputLength; i++) {
      outputData[i] = renderedData[trimStart + i]
    }
  }

  return output
}
